<template>
  <div class="embed-elevation-profile" :class="{ minimized: isMinimized }">
    <div class="profile-header" @click="toggleMinimize">
      <div class="profile-stats">
        <span class="stat-item">
          <span class="material-icons">straighten</span>
          {{ formatDistance(totalDistance) }}
        </span>
        <span class="stat-item">
          <span class="material-icons">trending_up</span>
          {{ formatElevation(totalElevationGain) }}
        </span>
        <span class="stat-item">
          <span class="material-icons">schedule</span>
          {{ formatDuration(totalDuration) }}
        </span>
      </div>
      <button class="minimize-btn" type="button" :title="isMinimized ? $t('hike_map_embed.embed_elevation_profile.show_chart') : $t('hike_map_embed.embed_elevation_profile.hide_chart')">
        <span class="material-icons">{{ isMinimized ? 'expand_less' : 'expand_more' }}</span>
      </button>
    </div>

    <div class="profile-content" v-show="!isMinimized">
      <div class="chart-container" ref="chartContainer">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, markRaw, type PropType } from 'vue';
import { Chart, registerables } from 'chart.js';
import mapboxgl from 'mapbox-gl';
import { distance as turfDistance } from '@turf/turf';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { elevationGainFromCoordinates } from '../../../utils/elevation/elevationGain';
import {
  elevationGainMetersForActiveView,
  durationMinutesForActiveView
} from '../../../utils/elevation/plannedTrailStats';
import {
  resolveDistanceMeters,
  resolveDurationSeconds,
  resolveElevationGainMeters
} from '../../../utils/elevation/embedElevationTotals';

Chart.register(...registerables);

let elevationMarker: mapboxgl.Marker | null = null;

export default defineComponent({
  name: 'EmbedElevationProfile',
  props: {
    geojson: {
      type: Object as PropType<any>,
      default: null
    },
    activeView: {
      type: Object as PropType<{ type: string; dayNumber?: number; trailId?: string | number }>,
      default: () => ({ type: 'full' })
    },
    plannedTrails: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    hikeDays: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    minimized: {
      type: Boolean,
      default: false
    },
    // Server-computed totals for callers that already know them (the recording
    // embed). Hikes leave these null and keep deriving from plannedTrails /
    // geometry exactly as before.
    distanceMetersOverride: {
      type: Number as PropType<number | null>,
      default: null
    },
    elevationGainMetersOverride: {
      type: Number as PropType<number | null>,
      default: null
    },
    durationSecondsOverride: {
      type: Number as PropType<number | null>,
      default: null
    }
  },
  emits: ['update:minimized'],
  data() {
    return {
      isMinimized: this.minimized,
      chartInstance: null as Chart<'line', number[], string> | null,
      totalDistance: 0,
      totalElevationGain: 0,
      totalDuration: 0
    };
  },
  computed: {
    filteredFeatures(): any[] {
      if (!this.geojson?.features) return [];

      let features = this.geojson.features;

      if (this.activeView.type === 'day') {
        features = features.filter((f: any) =>
          f.properties?.hikeDayNumber === this.activeView.dayNumber &&
          f.properties?.routeStyle === 'route'
        );
      } else if (this.activeView.type === 'route') {
        features = features.filter((f: any) =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(this.activeView.trailId)
        );
      } else {
        features = features.filter((f: any) => f.properties?.routeStyle === 'route');
      }

      return features;
    },

    multiLineStrings(): any[] {
      return this.filteredFeatures.filter(f => f.geometry?.type === 'MultiLineString');
    },

    chartData(): any {
      const multiLineStrings = this.multiLineStrings;
      if (!multiLineStrings.length) return null;

      const measurementUtils = MeasurementPrefUtility.preference();
      const allCoordinates: number[][] = [];

      multiLineStrings.forEach(feature => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates.forEach((lineString: number[][]) => {
            allCoordinates.push(...lineString);
          });
        }
      });

      if (!allCoordinates.length) return null;

      const distances: number[] = [];
      // {x, y} points keyed on real cumulative distance — plotted on a linear
      // axis so long straight (sparse) segments don't collapse into vertical
      // "steps" the way an index-spaced category axis made them.
      const points: { x: number; y: number }[] = [];
      const labels: string[] = [];
      const lats: number[] = [];
      const lons: number[] = [];
      let totalDistance = 0;

      allCoordinates.forEach((coord, index) => {
        if (index > 0) {
          const segmentDistance = turfDistance(
            allCoordinates[index - 1].slice(0, 2),
            coord.slice(0, 2),
            { units: 'meters' }
          );
          totalDistance += segmentDistance;
        }

        const elevation = coord[2] || 0;
        distances.push(totalDistance);
        points.push({ x: measurementUtils.bigUnitValue(totalDistance / 1000), y: measurementUtils.smallUnitValue(elevation) });
        labels.push(measurementUtils.bigUnitFormatter(totalDistance / 1000, false));
        lons.push(coord[0]);
        lats.push(coord[1]);
      });

      // Use the server-computed ptv2PlannedTrail.elevationGainMeters — the same
      // figure the hike page, the routes panel and the apps show. Summing raw
      // positive deltas off the plotted coordinates counted DEM sample noise and
      // reported a much bigger climb for the same route. Fall back to the
      // geometry (with the canonical 6m threshold) only when the planned-trail
      // data isn't there.
      const storedElevationGain = elevationGainMetersForActiveView(this.plannedTrails, this.activeView);

      this.totalDistance = resolveDistanceMeters(this.distanceMetersOverride, totalDistance);
      this.totalElevationGain = resolveElevationGainMeters(
        this.elevationGainMetersOverride,
        storedElevationGain,
        elevationGainFromCoordinates(allCoordinates)
      );
      this.updateDuration();

      // Simplify for performance
      const maxPoints = 150;
      const step = Math.max(1, Math.floor(points.length / maxPoints));

      return {
        labels: labels.filter((_, i) => i % step === 0 || i === labels.length - 1),
        datasets: [{
          label: measurementUtils.meterSymbol(),
          data: points.filter((_, i) => i % step === 0 || i === points.length - 1),
          borderColor: '#FFDF46',
          backgroundColor: 'rgba(255, 223, 70, 0.15)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.2,
          fill: true
        }],
        lat: lats.filter((_, i) => i % step === 0 || i === lats.length - 1),
        lon: lons.filter((_, i) => i % step === 0 || i === lons.length - 1)
      };
    }
  },
  watch: {
    activeView: {
      deep: true,
      handler() {
        this.updateChart();
      }
    },
    geojson: {
      deep: true,
      handler() {
        this.updateChart();
      }
    },
    minimized(newVal) {
      this.isMinimized = newVal;
    }
  },
  methods: {
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      this.$emit('update:minimized', this.isMinimized);
      if (!this.isMinimized) {
        this.$nextTick(() => this.updateChart());
      }
    },

    updateDuration() {
      // Same view filtering as the elevation gain total: a route view takes that
      // route's own duration whatever its style, day and full views sum only the
      // main 'route' segments.
      this.totalDuration = resolveDurationSeconds(
        this.durationSecondsOverride,
        durationMinutesForActiveView(this.plannedTrails, this.activeView)
      );
    },

    formatDistance(meters: number): string {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(meters / 1000, true);
    },

    formatElevation(meters: number): string {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(meters, false);
    },

    formatDuration(seconds: number): string {
      const minutes = seconds / 60;
      if (!minutes) return '0h';
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      if (hours === 0) return `${mins}m`;
      if (mins === 0) return `${hours}h`;
      return `${hours}h ${mins}m`;
    },

    createElevationMarker(): mapboxgl.Marker {
      const el = document.createElement('div');
      el.className = 'embed-elevation-marker';
      el.style.cssText = 'width:10px;height:10px;background:#FFDF46;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3)';
      return new mapboxgl.Marker(el);
    },

    removeElevationMarker() {
      if (elevationMarker) {
        elevationMarker.remove();
        elevationMarker = null;
      }
    },

    updateChart() {
      nextTick(() => {
        if (this.$refs.chartCanvas && this.chartData) {
          this.drawChart();
        } else {
          this.destroyChartInstance();
        }
      });
    },

    drawChart() {
      const chartData = this.chartData;
      if (!chartData) {
        this.destroyChartInstance();
        return;
      }

      const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      this.chartInstance = markRaw(new Chart(ctx, {
        type: 'line',
        data: {
          labels: chartData.labels.slice(),
          datasets: chartData.datasets.map((ds: any) => ({ ...ds, data: ds.data.slice() })),
          lat: chartData.lat.slice(),
          lon: chartData.lon.slice()
        } as any,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          animation: false,
          onHover: (event: any, activeElements: any[], chart: Chart) => {
            if (event.type === 'mouseout' || !activeElements?.length) {
              this.removeElevationMarker();
              return;
            }

            const hoveredIndex = activeElements[0].index;
            const data = chart.data as any;

            if (data.lat && data.lon && hoveredIndex < data.lat.length) {
              const lat = data.lat[hoveredIndex];
              const lon = data.lon[hoveredIndex];

              if (!elevationMarker) {
                elevationMarker = this.createElevationMarker();
              }

              if ((window as any).map) {
                try {
                  elevationMarker.setLngLat([lon, lat]).addTo((window as any).map);
                } catch (error) {
                  this.removeElevationMarker();
                }
              }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(40, 42, 50, 0.95)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: '#FFDF46',
              borderWidth: 1,
              padding: 6,
              displayColors: false,
              titleFont: { size: 10 },
              bodyFont: { size: 10 },
              callbacks: {
                title: (items) => {
                  const pref = MeasurementPrefUtility.preference();
                  return `${items[0].parsed.x} ${pref.kilometerSymbol()}`;
                },
                label: (context) => {
                  const pref = MeasurementPrefUtility.preference();
                  return `${pref.smallUnitFormatter(context.parsed.y, false)}`;
                }
              }
            }
          },
          scales: {
            x: {
              // Linear distance axis ({x, y} points carry real distance), pinned
              // to the route end so there's no empty stretch at the tail.
              type: 'linear',
              min: 0,
              max: (chartData.datasets?.[0]?.data?.length
                ? (chartData.datasets[0].data[chartData.datasets[0].data.length - 1] as any).x
                : undefined),
              display: true,
              grid: { color: 'rgba(255, 255, 255, 0.08)' },
              ticks: {
                color: 'rgba(255, 255, 255, 0.5)', font: { size: 9 }, maxTicksLimit: 6,
                callback: (v: number | string) => `${v} ${MeasurementPrefUtility.preference().kilometerSymbol()}`
              }
            },
            y: {
              display: true,
              beginAtZero: false,
              grid: { color: 'rgba(255, 255, 255, 0.08)' },
              ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 9 } }
            }
          }
        }
      }));

      canvas?.addEventListener('mouseleave', this.removeElevationMarker);
    },

    destroyChartInstance() {
      if (!this.chartInstance) return;

      const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;
      canvas?.removeEventListener('mouseleave', this.removeElevationMarker);

      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
  mounted() {
    this.updateChart();
  },
  beforeUnmount() {
    this.destroyChartInstance();
    this.removeElevationMarker();
  }
});
</script>

<style scoped>
.embed-elevation-profile {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  height: 120px;
  background: rgba(40, 42, 50, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  transition: height 0.2s ease;
  color: #fff;
  overflow: hidden;
}

.embed-elevation-profile.minimized {
  height: 36px;
}

.profile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: rgba(30, 32, 38, 0.7);
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  min-height: 36px;
}

.embed-elevation-profile.minimized .profile-header {
  border-radius: 8px;
}

.profile-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
}

.stat-item .material-icons {
  font-size: 14px;
  color: #FFDF46;
}

.minimize-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;
}

.minimize-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.minimize-btn .material-icons {
  font-size: 18px;
}

.profile-content {
  flex: 1;
  padding: 4px 8px 6px;
  overflow: hidden;
}

.chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .embed-elevation-profile {
    max-width: none;
    height: 100px;
  }

  .embed-elevation-profile.minimized {
    height: 32px;
  }

  .profile-stats {
    gap: 8px;
  }

  .stat-item {
    font-size: 10px;
  }

  .stat-item .material-icons {
    font-size: 12px;
  }
}
</style>
